/**
 * CR588: Merchant Anchor Intelligence Dashboard
 * Aggregated anchor performance per merchant for prompt pack tuning.
 */
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Search, ArrowUpDown, Copy, BarChart3 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface AnchorStat {
  id: string;
  merchant_source_id: string;
  token: string;
  token_type: "must" | "should" | "suggested";
  hit_count: number;
  miss_count: number;
  total_evaluated: number;
  last_seen_at: string | null;
}

type Recommendation = "promote" | "demote" | "remove" | "keep" | "strong_candidate";

function getRecommendation(stat: AnchorStat): Recommendation {
  const hitRate = stat.total_evaluated > 0 ? stat.hit_count / stat.total_evaluated : 0;
  if (stat.token_type === "suggested") {
    return stat.total_evaluated >= 5 && hitRate >= 0.6 ? "strong_candidate" : "keep";
  }
  if (stat.token_type === "must" && hitRate < 0.15 && stat.total_evaluated > 8) return "remove";
  if (stat.token_type === "must" && hitRate < 0.4 && stat.total_evaluated > 5) return "demote";
  if (stat.token_type === "should" && hitRate > 0.8 && stat.total_evaluated > 5) return "promote";
  return "keep";
}

const REC_CONFIG: Record<Recommendation, { label: string; emoji: string; className: string }> = {
  promote: { label: "Promote", emoji: "🟢", className: "border-emerald-500/50 text-emerald-700 dark:text-emerald-400" },
  demote: { label: "Demote", emoji: "🟡", className: "border-amber-500/50 text-amber-700 dark:text-amber-400" },
  remove: { label: "Remove", emoji: "🔴", className: "border-destructive/50 text-destructive" },
  keep: { label: "Keep", emoji: "⚪", className: "border-muted-foreground/30 text-muted-foreground" },
  strong_candidate: { label: "Strong candidate", emoji: "💡", className: "border-sky-500/50 text-sky-700 dark:text-sky-400" },
};

type SortKey = "token" | "hit_rate" | "total" | "recommendation";

export default function MerchantAnchorIntelligence() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const merchantFilter = searchParams.get("merchant") ?? "";

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "must" | "should" | "suggested">("all");
  const [sortKey, setSortKey] = useState<SortKey>("hit_rate");
  const [sortAsc, setSortAsc] = useState(false);

  const { data: stats, isLoading } = useQuery({
    queryKey: ["merchant-anchor-stats", merchantFilter],
    queryFn: async () => {
      let q = supabase
        .from("merchant_anchor_stats" as any)
        .select("*")
        .order("total_evaluated", { ascending: false })
        .limit(500);
      if (merchantFilter) {
        q = q.eq("merchant_source_id", merchantFilter);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as AnchorStat[];
    },
  });

  // Unique merchants for summary
  const merchants = useMemo(() => {
    if (!stats) return [];
    const map = new Map<string, { total: number; actionable: number }>();
    for (const s of stats) {
      const rec = getRecommendation(s);
      const prev = map.get(s.merchant_source_id) ?? { total: 0, actionable: 0 };
      prev.total++;
      if (rec !== "keep") prev.actionable++;
      map.set(s.merchant_source_id, prev);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ id, ...v }));
  }, [stats]);

  const filtered = useMemo(() => {
    if (!stats) return [];
    return stats
      .filter(s => typeFilter === "all" || s.token_type === typeFilter)
      .filter(s => !search || s.token.toLowerCase().includes(search.toLowerCase()))
      .map(s => ({ ...s, hitRate: s.total_evaluated > 0 ? s.hit_count / s.total_evaluated : 0, rec: getRecommendation(s) }))
      .sort((a, b) => {
        let cmp = 0;
        if (sortKey === "token") cmp = a.token.localeCompare(b.token);
        else if (sortKey === "hit_rate") cmp = a.hitRate - b.hitRate;
        else if (sortKey === "total") cmp = a.total_evaluated - b.total_evaluated;
        else if (sortKey === "recommendation") cmp = a.rec.localeCompare(b.rec);
        return sortAsc ? cmp : -cmp;
      });
  }, [stats, typeFilter, search, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const handleCopyAll = () => {
    const actionable = filtered.filter(s => s.rec !== "keep");
    const blob = {
      promote: actionable.filter(s => s.rec === "promote").map(s => s.token),
      demote: actionable.filter(s => s.rec === "demote").map(s => s.token),
      remove: actionable.filter(s => s.rec === "remove").map(s => s.token),
      strong_candidates: actionable.filter(s => s.rec === "strong_candidate").map(s => s.token),
    };
    navigator.clipboard.writeText(JSON.stringify(blob, null, 2));
    toast({ title: "Copied", description: `${actionable.length} actionable recommendations copied` });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Anchor Intelligence
            <Badge variant="outline" className="text-[10px]">CR588</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">
            Aggregated anchor performance across S1.4 runs — data-driven prompt pack tuning.
          </p>
        </div>
      </div>

      {/* Summary */}
      {!merchantFilter && merchants.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg border bg-card">
            <div className="text-2xl font-bold">{merchants.length}</div>
            <div className="text-xs text-muted-foreground">Merchants tracked</div>
          </div>
          <div className="p-3 rounded-lg border bg-card">
            <div className="text-2xl font-bold">{stats?.length ?? 0}</div>
            <div className="text-xs text-muted-foreground">Total anchor tokens</div>
          </div>
          <div className="p-3 rounded-lg border bg-card">
            <div className="text-2xl font-bold">{filtered.filter(s => s.rec !== "keep").length}</div>
            <div className="text-xs text-muted-foreground">Actionable recommendations</div>
          </div>
          <div className="p-3 rounded-lg border bg-card">
            <div className="text-2xl font-bold">{stats?.filter(s => s.token_type === "suggested").length ?? 0}</div>
            <div className="text-xs text-muted-foreground">Suggested candidates</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tokens..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="must">Must-hit</SelectItem>
            <SelectItem value="should">Should-hit</SelectItem>
            <SelectItem value="suggested">Suggested</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="gap-1" onClick={handleCopyAll}>
          <Copy className="h-3.5 w-3.5" /> Copy actionable
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading anchor stats…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No anchor stats yet. Run S1.4 on some deals to populate data.
        </div>
      ) : (
        <div className="rounded-md border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("token")}>
                  <div className="flex items-center gap-1">Token <ArrowUpDown className="h-3 w-3" /></div>
                </TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("hit_rate")}>
                  <div className="flex items-center gap-1">Hit Rate <ArrowUpDown className="h-3 w-3" /></div>
                </TableHead>
                <TableHead>Hits</TableHead>
                <TableHead>Misses</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("total")}>
                  <div className="flex items-center gap-1">Total <ArrowUpDown className="h-3 w-3" /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("recommendation")}>
                  <div className="flex items-center gap-1">Recommendation <ArrowUpDown className="h-3 w-3" /></div>
                </TableHead>
                {!merchantFilter && <TableHead>Merchant</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const rec = REC_CONFIG[s.rec];
                return (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.token}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{s.token_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              s.hitRate >= 0.8 ? 'bg-emerald-500' :
                              s.hitRate >= 0.5 ? 'bg-amber-500' :
                              'bg-destructive'
                            }`}
                            style={{ width: `${Math.round(s.hitRate * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">{(s.hitRate * 100).toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{s.hit_count}</TableCell>
                    <TableCell className="text-xs">{s.miss_count}</TableCell>
                    <TableCell className="text-xs">{s.total_evaluated}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${rec.className}`}>
                        {rec.emoji} {rec.label}
                      </Badge>
                    </TableCell>
                    {!merchantFilter && (
                      <TableCell className="text-[10px] font-mono text-muted-foreground max-w-[120px] truncate">
                        {s.merchant_source_id.slice(0, 8)}…
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
